/**
 * "This might be another way to buy it."
 *
 * A Related Products card says "N ways to buy it". The obvious next question
 * — is there an N+1 the catalog already suspects? — had its answer one screen
 * away, in a workspace that talks about substitutes, in a card shape nobody
 * could act on. The Luxardo maraschino cherries were the case: the group card
 * read "2 ways to buy it", while the fact that a third cherry product had
 * been classified as related to it lived in Substitutes & Alternatives as a
 * two-tile card whose tiles were a collapsed concept and a lone item.
 *
 * So the evidence comes to the group instead. Every card with a potential
 * sibling (see potentialSiblingsForGroup) carries a pill; opening it lists
 * the candidates with what the catalog already thinks of each, and offers the
 * two answers a person actually has:
 *
 *   ADD TO GROUP  — a confirmed same_item link to the group's canonical
 *                   member (createManualRelationship, which upserts the pair,
 *                   so promoting an existing "alternative" row retypes it
 *                   rather than growing a second row). Never a merge: both
 *                   items keep their catalog rows and every vendor product
 *                   the newcomer owns joins through the existing fold.
 *
 *   NOT THE SAME  — for a SUGGESTED row only, rejectRelationship: the stored
 *                   human "no" that also stops the pair being re-suggested.
 *                   A CONFIRMED alternative is offered "Keep as alternative"
 *                   instead, which writes nothing at all — that row is
 *                   already filed exactly where it belongs, and rejecting it
 *                   to quiet a pill would destroy a real relationship.
 *
 * Adding runs the SAME server group-comparison pass every other membership
 * change runs. No price maths here.
 */
import React, { useState } from 'react';
import { createManualRelationship, rejectRelationship } from '../services/itemRelationshipService';
import { requestGroupComparison } from '../services/comparisonRecomputeService';
import { RELATIONSHIP_TYPE_LABELS } from '../utils/relatedProductCatalog';

const text = (value) => String(value ?? '').trim();

const money = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? `$${parsed.toFixed(2)}` : null;
};

const styles = {
  panel: {
    borderTop: '1px solid #EEE5DF', background: '#FAF7F4', padding: '11px 13px 13px',
    display: 'grid', gap: 8,
  },
  lede: { color: '#6B5A52', fontSize: 11.5, lineHeight: 1.45, margin: 0 },
  candidate: {
    border: '1px solid #E5DAD2', borderRadius: 10, background: '#fff', padding: '9px 10px',
    display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
  },
  body: { minWidth: 0, flex: '1 1 260px' },
  name: { color: '#3E2723', fontSize: 12.5, fontWeight: 750, lineHeight: 1.25 },
  meta: { color: '#7D6D64', fontSize: 10.5, marginTop: 3, lineHeight: 1.4 },
  why: { color: '#6B5A52', fontSize: 11, marginTop: 5, lineHeight: 1.4 },
  badgeRow: { display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 },
  badge: {
    display: 'inline-flex', padding: '2px 7px', borderRadius: 999, background: '#EEF3F5',
    color: '#526B7C', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em',
  },
  suggestedBadge: { background: '#EDE7F6', color: '#5E35B1' },
  actions: { display: 'flex', gap: 6, alignItems: 'flex-start', flexShrink: 0 },
  working: { color: '#8A5A00', fontSize: 11.5, fontWeight: 700 },
  error: {
    background: '#FDECEA', border: '1px solid #F5C2BD', color: '#B71C1C',
    borderRadius: 8, padding: '8px 10px', fontSize: 12,
  },
};

/** The vendor products a candidate item would bring with it. */
const vendorSummary = (itemId, optionsByItemId, vendorNameById) => (
  (optionsByItemId?.[itemId] || [])
    .filter((option) => option && option.status !== 'inactive')
    .map((option) => [
      text(vendorNameById?.[option.vendor_id]) || text(option.vendor_name_text) || 'Vendor not set',
      text(option.pack_description),
      money(option.package_price),
    ].filter(Boolean).join(' · '))
);

export default function PotentialSiblingsPanel({
  group,
  siblings = [],
  itemById = new Map(),
  optionsByItemId = {},
  vendorNameById = {},
  storeId,
  onChanged,
}) {
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState([]);

  const visible = siblings.filter((entry) => !dismissed.includes(String(entry.id)));
  if (!siblings.length) return null;

  const memberItemIds = group.memberItemIds?.length ? group.memberItemIds : [group.itemId];
  const anchorId = group.canonicalItemId ?? group.itemId;
  const busy = busyId !== '';

  const nameOf = (entry) => (
    text(entry.otherItem?.description)
    || text(itemById.get?.(String(entry.otherItemId))?.description)
    || `Item ${entry.otherItemId}`
  );

  const addToGroup = async (entry) => {
    setBusyId(String(entry.id));
    setError('');
    try {
      await createManualRelationship(storeId, anchorId, entry.otherItemId, 'same_item');
    } catch (addError) {
      setError(addError?.message || 'That product could not be added to the group.');
      setBusyId('');
      return;
    }
    // The membership changed, so the group's denominator and comparable
    // prices are stale until the server recomputes them. Same pass as every
    // other membership change.
    try {
      await requestGroupComparison(storeId, [...memberItemIds, entry.otherItemId]);
    } catch (recomputeError) {
      // The link is saved either way; the card simply shows the previous
      // comparison until the next recompute.
    }
    // The write and server recompute are the action this button owns. Parent
    // refreshes may be slow or fail independently; never leave this control
    // stuck on "Adding…" while waiting for one.
    setBusyId('');
    try {
      if (typeof onChanged === 'function') await onChanged();
    } catch (refreshError) {
      setError('Added. Refresh the catalog to see it.');
    }
  };

  const notTheSame = async (entry) => {
    setBusyId(String(entry.id));
    setError('');
    try {
      await rejectRelationship(entry.id);
      setBusyId('');
      if (typeof onChanged === 'function') await onChanged();
    } catch (rejectError) {
      setError(rejectError?.message || 'That suggestion could not be dismissed.');
      setBusyId('');
    }
  };

  return (
    <div style={styles.panel} data-testid="potential-siblings-panel">
      <p style={styles.lede}>
        Products the catalog already links to <strong>{group.canonicalName}</strong> that are not
        in this group. Adding one links it — both items keep their own catalog rows, and every
        vendor product it owns becomes another way to buy this.
      </p>
      {error && <div style={styles.error} role="alert" data-testid="potential-siblings-error">{error}</div>}
      {!visible.length ? (
        <div style={styles.lede} data-testid="potential-siblings-empty">
          Nothing else to review here.
        </div>
      ) : visible.map((entry) => {
        const vendors = vendorSummary(entry.otherItemId, optionsByItemId, vendorNameById);
        const confidence = Number.parseFloat(entry.confidence);
        const suggested = entry.status === 'suggested';
        return (
          <div key={entry.id} style={styles.candidate} data-testid="potential-sibling">
            <div style={styles.body}>
              <div style={styles.name}>{nameOf(entry)}</div>
              {vendors.length > 0 && (
                <div style={styles.meta}>
                  {vendors.length} way{vendors.length === 1 ? '' : 's'} to buy · {vendors.join(' · ')}
                </div>
              )}
              <div style={styles.badgeRow}>
                <span style={{ ...styles.badge, ...(suggested ? styles.suggestedBadge : {}) }}>
                  {suggested ? 'Suggested' : 'Confirmed'}
                </span>
                <span style={styles.badge}>
                  {RELATIONSHIP_TYPE_LABELS[entry.relationshipType] || entry.relationshipType}
                </span>
                {Number.isFinite(confidence) && confidence > 0 && (
                  <span style={styles.badge}>{Math.round(confidence * 100)}% confident</span>
                )}
              </div>
              {entry.reasoning && <div style={styles.why}>{entry.reasoning}</div>}
            </div>
            <div style={styles.actions}>
              {busyId === String(entry.id) ? (
                <span style={styles.working} data-testid="potential-sibling-working">Adding…</span>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={busy}
                    data-testid="potential-sibling-add"
                    onClick={() => addToGroup(entry)}
                  >
                    Add to group
                  </button>
                  {suggested ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={busy}
                      data-testid="potential-sibling-reject"
                      onClick={() => notTheSame(entry)}
                    >
                      Not the same
                    </button>
                  ) : (
                    /* A confirmed alternative is already filed correctly.
                       Ignoring it must write NOTHING — rejecting the row to
                       quiet a pill would destroy a real relationship. */
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={busy}
                      data-testid="potential-sibling-keep"
                      onClick={() => setDismissed((current) => [...current, String(entry.id)])}
                    >
                      Keep as alternative
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        );
  })}
    </div>
  );
}
